

import React, {useCallback, useContext, useEffect, useState} from 'react';

import {BasicPage} from 'src/components/basic-page';
import {NumberInput} from 'src/components/input';
import {QueryArea} from 'src/components/query-area';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {
    DISCARD_CHANGES_SUCCESS_MESSAGE,
    MAX_NUMBER_VALUE,
    MAX_SELECTED_ACCOUNTS,
    SAVE_CHANGES_SUCCESS_MESSAGE,
} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContextProvider} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';

import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Group, Item} from '@pb/kit-component-group';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';


import {FREQUENCY_UNIT_OPTIONS, MAX_FREQUENCY_MAP, SECTION_NAME} from './constants';
import {FullStoryConfiguratorSkeleton} from './full-story-configurator.skeleton';
import {LastEditedLabel} from './last-edited-label';
import {useFullStorySettings} from './useFullStorySettings';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {FrequencyUnit} from 'src/openapi/admin/models/FrequencyUnit';
import type {Pill} from 'src/typedef';

const accountIdValidation = (id: string) => !isNaN(+id) && +id > 0 && +id < MAX_NUMBER_VALUE;

const IP4Validation = (ip: string) => {
    const regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return regex.test(ip);
};

const IP6Validation = (ip: string) => {
    const regex = /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;
    return regex.test(ip);
};

const FullStoryConfigurator = () => {
    const [busy, setBusy] = useState(false);
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);

    const {
        isLoading,
        loadingError,
        isChanged,
        data,
        setData,
        saveChanges: saveFullStoryChanges,
        discardChanges: discardFullStoryChanges,
        loadData: loadFullStorySettings,
    } = useFullStorySettings();

    const {
        proceed,
        setIsChunkChanged,
        isCurrentChunkChanged,
    } = useConfirmationChunk({chunkName: 'full-story', onProceed: () => {
        setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        loadFullStorySettings();
    }});

    useEffect(() => {
        setIsChunkChanged(isChanged);
    }, [isChanged, setIsChunkChanged]);

    const saveChanges = useCallback(async () => {
        const calls = [];

        setBusy(true);
        calls.push(await saveFullStoryChanges());
        setBusy(false);

        const errors = [...new Set(calls.filter(Boolean))];
        if (errors.length) {
            setErrorMessage(errors.join('\n'));
        } else {
            proceed();
        }
    }, [saveFullStoryChanges, proceed, setErrorMessage]);

    const discardChanges = useCallback(() => {
        discardFullStoryChanges();
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [discardFullStoryChanges, setSuccessMessage]);

    const changeSessionRecordPercentage = useCallback((value?: number) => {
        setData(prevState => ({...prevState, sessionRecordPercentage: value || 0}));
    }, [setData]);

    const changeSamplingFrequency = useCallback((value?: number) => {
        setData(prevState => ({...prevState, samplingFrequency: value || 0}));
    }, [setData]);

    const changeFrequencyUnit = useCallback((value: string) => {
        const frequencyUnit = value as FrequencyUnit;
        setData(prevState => ({
            ...prevState,
            frequencyUnit,
            samplingFrequency: prevState.samplingFrequency > MAX_FREQUENCY_MAP[frequencyUnit]
                ? MAX_FREQUENCY_MAP[frequencyUnit]
                : prevState.samplingFrequency,
        }));

    }, [setData]);

    const onSetAccountIds = useCallback((accountIdsPills: Pill<string>[]) => {
        setData(prevState => ({
            ...prevState,
            accountsIds: [
                ...prevState.accountsIds ? prevState.accountsIds : [],
                ...accountIdsPills
                    .filter(pill => accountIdValidation(pill.value))
                    .map(({value}) => +value),
            ],
        }));
    }, [setData]);

    const onRemoveAccountId = useCallback((accountId: string) => {
        setData(prevState => ({
            ...prevState,
            accountsIds: prevState.accountsIds?.filter(id => id !== +accountId),
        }));
    }, [setData]);

    const onSetIPs = useCallback((IPsPills: Pill<string>[]) => {
        setData(prevState => ({
            ...prevState,
            ips: [
                ...prevState.ips ? prevState.ips : [],
                ...IPsPills
                    .filter(pill => IP4Validation(pill.value) || IP6Validation(pill.value))
                    .map(({value}) => value),
            ],
        }));
    }, [setData]);

    const onRemoveIP = useCallback((value: string) => {
        setData(prevState => ({
            ...prevState,
            ips: prevState.ips?.filter(ip => ip !== value),
        }));
    }, [setData]);

    return (
        <Skeleton shown={isLoading} view={<FullStoryConfiguratorSkeleton/>}>
            <ServerResponseHandler
                serverError={loadingError}
                accessRight={ACCESS_RIGHTS.FULL_STORY_CONFIGURATOR as AccessRight}
                sectionName={SECTION_NAME}>
                <BasicPage
                    title={SECTION_NAME}
                    buttonsBlock={(
                        <Group>
                            <Button
                                disabled={!isCurrentChunkChanged || busy}
                                variant="Type.SECONDARY"
                                onClick={discardChanges}>
                                Discard Changes
                            </Button>
                            <Button
                                busy={busy}
                                disabled={!isCurrentChunkChanged}
                                variant="Type.PRIMARY"
                                onClick={saveChanges}>
                                Save Changes
                            </Button>
                        </Group>
                    )}>
                    <Item className="group-item pd-l">
                        <Group direction="v">
                            <Group alignItems="center">
                                <Item style={{width: 300}}>
                                    <Info
                                        type={{popover:
                                            'Session to Record = chance of a user being selected to recording\n' +
                                            'e.g. 15% = a user has a 15% chance of being recorded',
                                        }}>
                                        <Label>Sessions to Record</Label>
                                    </Info>
                                </Item>
                                <Group alignItems="center">
                                    <NumberInput
                                        width={70}
                                        value={data.sessionRecordPercentage}
                                        minValue={0}
                                        maxValue={100}
                                        onBlur={changeSessionRecordPercentage}
                                        displayedValue={data.sessionRecordPercentage.toString()}
                                    />
                                    <span>%</span>
                                </Group>
                            </Group>
                            <Group alignItems="center">
                                <Item style={{width: 300}}>
                                    <Info
                                        type={{popover:
                                            'Sampling Frequency = time between refreshing the sample chosen for ' +
                                            'recording\ne.g. 1h = a new sample will be chosen every hour',
                                        }}>
                                        <Label>Sampling Frequency</Label>
                                    </Info>
                                </Item>
                                <Group alignItems="center">
                                    <NumberInput
                                        width={70}
                                        value={data.samplingFrequency}
                                        minValue={0}
                                        maxValue={MAX_FREQUENCY_MAP[data.frequencyUnit]}
                                        onBlur={changeSamplingFrequency}
                                        displayedValue={data.samplingFrequency.toString()}
                                    />
                                    <Item style={{width: 150}}>
                                        <ComboBox value={data.frequencyUnit} onChange={changeFrequencyUnit}>
                                            {FREQUENCY_UNIT_OPTIONS.map(({code, description}) => (
                                                <ComboboxItem key={code} value={code} caption={description}/>
                                            ))}
                                        </ComboBox>
                                    </Item>
                                </Group>
                            </Group>
                            <Item style={{width: 600}}>
                                <Info type={{popover: 'Record all sessions of users from the account below'}}>
                                    <Label>Account IDs</Label>
                                </Info>
                                <QueryArea
                                    placeholder="Enter account IDs"
                                    items={data.accountsIds
                                        ? data.accountsIds.map(value => ({value}))
                                        : []}
                                    onValidate={accountIdValidation}
                                    onAdd={onSetAccountIds}
                                    onRemove={onRemoveAccountId}
                                    maxSelected={MAX_SELECTED_ACCOUNTS}
                                />
                            </Item>
                            <Item style={{width: 600}}>
                                <Info type={{popover: 'Record all sessions of users from the IP addresses below'}}>
                                    <Label>IP Addresses</Label>
                                </Info>
                                <QueryArea
                                    placeholder="Enter IP Adresses"
                                    items={data.ips
                                        ? data.ips.map(value => ({value}))
                                        : []}
                                    onValidate={value => IP4Validation(value) || IP6Validation(value)}
                                    onAdd={onSetIPs}
                                    onRemove={onRemoveIP}
                                    maxSelected={MAX_SELECTED_ACCOUNTS}
                                />
                            </Item>
                            {data.editorId
                                ? <LastEditedLabel editorId={data.editorId} lastEdited={data.editedAt}/>
                                : null}
                        </Group>
                    </Item>
                </BasicPage>
            </ServerResponseHandler>
        </Skeleton>
    );
};


export const ValidateFullStoryConfiguration = () => (
    <ValidationContextProvider>
        <FullStoryConfigurator/>
    </ValidationContextProvider>
);
